/**
 * Hook: scroll depth (25/50/75) and time on page (10s/30s).
 * Fires each event once per page (resets on route change).
 * Use in App or layout so it runs on every virtual page.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pushScrollDepth, pushTimeOnPage } from '../utils/dataLayer';

export function usePageEngagement() {
  const location = useLocation();
  const scrollFired = useRef({ 25: false, 50: false, 75: false });
  const timeFired = useRef({ 10: false, 30: false });
  const timeoutsRef = useRef([]);

  // Reset flags when route changes (each "page" gets its own engagement)
  useEffect(() => {
    scrollFired.current = { 25: false, 50: false, 75: false };
    timeFired.current = { 10: false, 30: false };
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    const t10 = setTimeout(() => {
      if (!timeFired.current[10]) {
        timeFired.current[10] = true;
        pushTimeOnPage(10);
      }
    }, 10000);
    const t30 = setTimeout(() => {
      if (!timeFired.current[30]) {
        timeFired.current[30] = true;
        pushTimeOnPage(30);
      }
    }, 30000);
    timeoutsRef.current = [t10, t30];

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [location.pathname]);

  // Scroll depth: fire once per threshold per page
  useEffect(() => {
    const handleScroll = () => {
      const doc = document.documentElement;
      const scrollTop = doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      if (scrollHeight <= 0) return;
      const pct = Math.round((scrollTop / scrollHeight) * 100);
      [25, 50, 75].forEach((threshold) => {
        if (pct >= threshold && !scrollFired.current[threshold]) {
          scrollFired.current[threshold] = true;
          pushScrollDepth(threshold);
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
}
